import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { Spin } from "antd";

import css from "./CycleDayMenuModal.module.css";
import { cycleAvailableDishesActions, cycleDayMenuActions } from "../../../store/slices/menu-slices";
import { cycleMenuService } from "../../../services/menu-services/cycleMenu.service";
import { ImageReusable } from "../../../ui";

const CycleDayMenuModal = ({ setIsModalOpen, cycleDay }) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const { dishesArray: availableDishesArray, status: dishesStatus } = useSelector((store) => store.cycleAvailableDishes);
    const isDishesLoading = dishesStatus === "loading" || dishesStatus === null;
    const { activePackId, activeMealId, activeIdMatching } =
        useSelector((store) => store.cycleDayMenu);

    const [query, setQuery] = useState("");

    useEffect(() => {
        dispatch(cycleAvailableDishesActions.getAllCycleAvailableDishes(cycleDay));
    }, [cycleDay, dispatch]);

    const dishes = (availableDishesArray || []).filter((obj) => {
        if (!query.trim()) return true;
        const name = obj.dish?.name || obj.name || {};
        const displayName = name[currentLanguage] || name["en"] || "";
        return displayName.toLowerCase().includes(query.toLowerCase());
    });

    const closeModal = () => {
        dispatch(cycleDayMenuActions.setActiveIdMatching(null));
        setIsModalOpen(false);
    };

    const handleSelectDish = async (obj) => {
        try {
            const data = {
                cycleDay,
                packId: activePackId,
                mealId: activeMealId,
                dishId: obj.dishId,
                type: obj.dish?.type,
            };

            // Backend create uses upsert (cycleDay+packId+mealId key), so always safe to call
            await cycleMenuService.create(data);

            dispatch(cycleDayMenuActions.changeTrigger());
            dispatch(cycleDayMenuActions.setActiveIdMatching(null));
            setIsModalOpen(false);
            toast.success(t("Dish assigned successfully"));
        } catch (error) {
            console.error(error);
            toast.error(t("Failed to assign dish"));
        }
    };

    return (
        <div className={css.overlay} onClick={closeModal}>
            <div className={css.modal} onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className={css.header}>
                    <h2>{t("Select dish")} — Day {cycleDay}</h2>
                    <button className={css.closeButton} onClick={closeModal}>
                        <X size={18} />
                    </button>
                </div>

                {/* Search */}
                <div className={css.searchBar}>
                    <Search size={16} className={css.searchIcon} />
                    <input
                        className={css.searchInput}
                        placeholder={t("Search dishes...")}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        autoFocus
                    />
                    {query && (
                        <button className={css.clearBtn} onClick={() => setQuery("")}>
                            Clear
                        </button>
                    )}
                </div>

                {/* Grid */}
                <div className={css.grid}>
                    {isDishesLoading ? (
                        <div className={css.empty}>
                            <Spin size="large" />
                        </div>
                    ) : dishes.length === 0 ? (
                        <div className={css.empty}>
                            <Search size={32} opacity={0.3} />
                            <span>{t("No dishes found")}</span>
                        </div>
                    ) : (
                        dishes.map((obj) => {
                            const name = obj.dish?.name || obj.name || {};
                            const displayName = name[currentLanguage] || name["en"] || "";
                            const img = obj.dish?.img || obj.img;
                            const cost = obj.dish?.cost ?? obj.cost;
                            const price = obj.dish?.price ?? obj.price;
                            const weight = obj.dish?.weight ?? obj.weight;
                            const categories = obj.dish?.categories || obj.categories || [];
                            const categoryName = categories
                                .map((c) => c.name?.[currentLanguage] || c.name?.["en"] || "")
                                .filter(Boolean)
                                .join(", ");

                            return (
                                <div
                                    key={obj.id}
                                    className={css.card}
                                    onClick={() => handleSelectDish(obj)}
                                    title={displayName}
                                >
                                    <ImageReusable
                                        src={img}
                                        alt={displayName}
                                        className={css.cardImage}
                                    />
                                    <div className={css.cardBody}>
                                        <div className={css.cardName}>{displayName}</div>
                                        {categoryName && (
                                            <span className={css.cardCategory}>{categoryName}</span>
                                        )}
                                        <div className={css.cardMeta}>
                                            {weight != null && (
                                                <div className={css.metaItem}>
                                                    <span className={css.metaLabel}>Weight</span>
                                                    <span className={css.metaValue}>{weight}g</span>
                                                </div>
                                            )}
                                            {cost != null && (
                                                <div className={css.metaItem}>
                                                    <span className={css.metaLabel}>Cost</span>
                                                    <span className={css.metaValue}>{Number(cost).toFixed(2)}</span>
                                                </div>
                                            )}
                                            {price != null && (
                                                <div className={css.metaItem}>
                                                    <span className={css.metaLabel}>Price</span>
                                                    <span className={`${css.metaValue} ${css.price}`}>{Number(price).toFixed(2)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default CycleDayMenuModal;
